import * as d3 from 'd3';
import { Color } from 'src/styles';

interface DrawDonutChartOptions {
  total: number;
  element: HTMLDivElement;
  data: number[];
  colors: string[];
  isLoading?: boolean;
  hasError?: boolean;
  error?: string;
}

interface BaseChartParams {
  element: HTMLDivElement;
  boxSize: number;
  border: number;
}

const clearChart = (element: HTMLDivElement) => {
  d3.select(element).select('svg').remove();
  d3.select(element).select('.donut-tooltip').remove();
  d3.select(element).style('position', 'relative');
};

const createSvg = (element: HTMLDivElement, boxSize: number) => {
  return d3
    .select(element)
    .append('svg')
    .attr('preserveAspectRatio', 'xMidYMid meet')
    .attr('height', '100%')
    .attr('width', '100%')
    .attr('viewBox', `0 0 ${boxSize} ${boxSize}`)
    .append('g')
    .attr('transform', `translate(${boxSize / 2}, ${boxSize / 2})`);
};

const renderLoadingState = ({ element, boxSize, border }: BaseChartParams) => {
  clearChart(element);
  const svg = createSvg(element, boxSize);

  const loadingArc = d3
    .arc()
    .innerRadius(boxSize / 2 - border)
    .outerRadius(boxSize / 2)
    .startAngle(0);

  const loadingPath = svg
    .append('path')
    .datum({ endAngle: 0 })
    .style('fill', Color.Brand6)
    .attr('d', loadingArc as any);

  // Animate loading arc
  function animateLoading() {
    loadingPath
      .transition()
      .duration(1500)
      .attrTween('d', function () {
        const interpolate = d3.interpolate(0, 2 * Math.PI);

        return function (t: number) {
          const arc = d3
            .arc()
            .innerRadius(boxSize / 2 - border)
            .outerRadius(boxSize / 2)
            .startAngle(0)
            .endAngle(interpolate(t));

          return arc(null as any) as string;
        };
      })
      .on('end', function () {
        // Fade out and restart
        d3.select(this)
          .transition()
          .duration(300)
          .style('opacity', 0)
          .on('end', function () {
            d3.select(this).style('opacity', 1);
            animateLoading();
          });
      });
  }

  animateLoading();

  // Loading text
  svg
    .append('text')
    .attr('dy', '-20px')
    .attr('text-anchor', 'middle')
    .style('font-size', '1.125rem')
    .style('font-weight', '400')
    .style('fill', Color.Gray7)
    .text('Loading...');
};

const renderErrorState = ({ element, boxSize, border, error }: BaseChartParams & { error?: string }) => {
  clearChart(element);
  const svg = createSvg(element, boxSize);

  const errorPie = d3.pie<number>().value(() => 1);

  const errorArcs = svg
    .selectAll('path')
    .data(errorPie([1]))
    .enter();

  errorArcs
    .append('path')
    .attr(
      'd',
      d3
        .arc()
        .innerRadius(boxSize / 2 - border)
        .outerRadius(boxSize / 2)
        .padAngle(0)
        .cornerRadius(8) as any,
    )
    .style('fill', Color.Red7)
    .style('stroke', '#fff')
    .style('stroke-width', '2px')
    .style('opacity', 0.6);

  // Create tooltip for error
  const errorTooltip = d3
    .select(element)
    .append('div')
    .attr('class', 'donut-error-tooltip')
    .style('position', 'absolute')
    .style('visibility', 'hidden')
    .style('background-color', 'rgba(0, 0, 0, 0.9)')
    .style('color', 'white')
    .style('border-radius', '4px')
    .style('padding', '8px 12px')
    .style('font-size', '14px')
    .style('pointer-events', 'none')
    .style('z-index', '1000')
    .style('box-shadow', '0 2px 4px rgba(0,0,0,0.2)')
    .style('max-width', '250px')
    .style('word-wrap', 'break-word');

  // Error icon group with hover interaction
  const errorIconGroup = svg.append('g').style('cursor', 'pointer');

  errorIconGroup
    .append('circle')
    .attr('r', 30)
    .attr('cy', -10)
    .style('fill', '#fff') // White background
    .style('stroke', Color.Red7) // Red border
    .style('stroke-width', '3px');

  errorIconGroup
    .append('text')
    .attr('text-anchor', 'middle')
    .attr('dy', -5)
    .style('font-size', '28px')
    .style('font-weight', 'bold')
    .style('fill', Color.Red7) // Red exclamation mark
    .style('pointer-events', 'none')
    .text('!');

  // Add hover interaction to the group
  errorIconGroup
    .on('mouseover', function () {
      d3.select(this).select('circle').style('opacity', 0.8);

      if (error) {
        errorTooltip.style('visibility', 'visible').html(`
          <div><strong>Error:</strong></div>
          <div>${error}</div>
        `);

        return;
      }

      errorTooltip.style('visibility', 'visible').html(`
        <div><strong>An unexpected error occurred. Please try again or contact clientservices@myipo.com for assistance.</strong></div>
      `);
    })
    .on('mousemove', function (event) {
      errorTooltip.style('top', `${event.offsetY - 10}px`).style('left', `${event.offsetX + 10}px`);
    })
    .on('mouseout', function () {
      d3.select(this).select('circle').style('opacity', 1);
      errorTooltip.style('visibility', 'hidden');
    });

  // Static error message
  svg
    .append('text')
    .attr('dy', '35px')
    .attr('text-anchor', 'middle')
    .style('font-size', '1rem')
    .style('font-weight', '600')
    .style('fill', Color.Red7)
    .text('Unexpected Error Occurred');

  svg
    .append('text')
    .attr('dy', '55px')
    .attr('text-anchor', 'middle')
    .style('font-size', '0.75rem')
    .style('font-weight', '400')
    .style('fill', Color.Gray7);
};

const renderNormalState = ({
  element,
  boxSize,
  border,
  total,
  data,
  colors,
}: BaseChartParams & { total: number; data: number[]; colors: string[] }) => {
  clearChart(element);

  const validData = data.map(d => Math.max(0, d));
  const actualTotal = validData.reduce((sum, val) => sum + val, 0);
  const isEmpty = actualTotal === 0;

  const svg = createSvg(element, boxSize);

  const arcGenerator: any = d3
    .arc()
    .padAngle(isEmpty ? 0 : 0.01)
    .cornerRadius(8)
    .innerRadius(boxSize / 2 - border)
    .outerRadius(boxSize / 2);

  const minPercentage = 0.01;

  const dataWithOriginal = isEmpty
    ? [{ original: 0, adjusted: 1, index: 0 }]
    : validData.map((value, index) => {
        const percentage = value / actualTotal;
        const adjustedValue = percentage > 0 && percentage < minPercentage ? actualTotal * minPercentage : value;

        return {
          original: value,
          adjusted: adjustedValue,
          index,
        };
      });

  const pieGenerator = d3.pie<any>().value(d => d.adjusted);
  const arcs = svg.selectAll('path').data(pieGenerator(dataWithOriginal)).enter();

  const valueLabel = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(total);

  // Create tooltip
  const tooltip = d3
    .select(element)
    .append('div')
    .attr('class', 'donut-tooltip')
    .style('position', 'absolute')
    .style('visibility', 'hidden')
    .style('background-color', 'rgba(0, 0, 0, 0.8)')
    .style('color', 'white')
    .style('border-radius', '4px')
    .style('padding', '8px 12px')
    .style('font-size', '14px')
    .style('pointer-events', 'none')
    .style('z-index', '1000')
    .style('box-shadow', '0 2px 4px rgba(0,0,0,0.2)');

  arcs
    .append('path')
    .attr('d', arcGenerator)
    .style('fill', (_d: any, i: number) => (isEmpty ? Color.Gray7 : colors[i % validData.length]))
    .style('stroke', '#fff')
    .style('stroke-width', '2px')
    .style('cursor', 'pointer')
    .on('mouseover', function (event, d: any) {
      const originalValue = d.data.original;
      const percentage = isEmpty ? 0 : ((originalValue / actualTotal) * 100).toFixed(2);
      const formattedValue = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(originalValue);

      d3.select(this).style('opacity', 0.8);

      tooltip.style('visibility', 'visible').html(`
          <div><strong>Value:</strong> ${formattedValue}</div>
          <div><strong>Percentage:</strong> ${percentage}%</div>
        `);
    })
    .on('mousemove', function (event) {
      tooltip.style('top', `${event.offsetY - 10}px`).style('left', `${event.offsetX + 10}px`);
    })
    .on('mouseout', function () {
      d3.select(this).style('opacity', 1);
      tooltip.style('visibility', 'hidden');
    });

  svg
    .append('text')
    .attr('dy', '-30px')
    .attr('text-anchor', 'middle')
    .style('font-size', '1.5rem')
    .style('line-height', '2.125rem')
    .style('font-weight', '400')
    .style('fill', '#545454')
    .text('Account Value')
    .transition()
    .duration(700);

  svg
    .append('text')
    .style('text-anchor', 'middle')
    .style('font-size', '28px')
    .style('font-family', 'Source Sans Pro')
    .style('font-style', 'normal')
    .style('font-weight', '700')
    .style('line-height', '36px')
    .style('fill', '#0255A3')
    .attr('class', 'data')
    .text(valueLabel);
};

export const drawDonutChart = ({
  total,
  element,
  data,
  colors,
  isLoading = false,
  hasError = false,
  error,
}: DrawDonutChartOptions) => {
  const boxSize = 275.5;
  const border = 14;

  if (isLoading) {
    renderLoadingState({ element, boxSize, border });

    return;
  }

  if (hasError) {
    renderErrorState({ element, boxSize, border, error });

    return;
  }

  renderNormalState({ element, boxSize, border, total, data, colors });
};
